/** Tiny in-memory food search: token prefix matching with simple scoring. Fast enough to run on every keystroke. */

export interface Searchable {
  ref: string;
  name: string;
  brand?: string | null;
  aliases?: string[];
}

export interface SearchIndex<T extends Searchable> {
  items: T[];
  tokens: string[][];
  names: string[];
}

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9%]+/g, ' ')
    .trim();
}

function singular(token: string): string {
  if (token.length > 4 && token.endsWith('ies')) return `${token.slice(0, -3)}y`;
  if (token.length > 3 && token.endsWith('es') && /(ch|sh|s|x|z|o)es$/.test(token)) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
  return token;
}

function tokenize(text: string): string[] {
  return normalize(text)
    .split(' ')
    .filter(Boolean)
    .map(singular);
}

export function buildIndex<T extends Searchable>(items: T[]): SearchIndex<T> {
  return {
    items,
    names: items.map((i) => normalize(i.name)),
    tokens: items.map((i) => [...new Set(tokenize([i.name, i.brand ?? '', ...(i.aliases ?? [])].join(' ')))]),
  };
}

export function searchIndex<T extends Searchable>(
  index: SearchIndex<T>,
  query: string,
  opts: { limit?: number; boost?: Map<string, number> } = {},
): T[] {
  const q = tokenize(query);
  if (q.length === 0) return [];
  const qNorm = normalize(query);
  const scored: Array<{ item: T; score: number }> = [];

  index.items.forEach((item, i) => {
    const tokens = index.tokens[i] ?? [];
    let score = 0;
    for (const qt of q) {
      let best = 0;
      for (const t of tokens) {
        if (t === qt) best = Math.max(best, 3);
        else if (t.startsWith(qt)) best = Math.max(best, 2);
        else if (qt.length >= 4 && t.includes(qt)) best = Math.max(best, 1);
      }
      if (best === 0) return; // every query word must match something
      score += best;
    }
    const name = index.names[i] ?? '';
    if (name === qNorm) score += 6;
    else if (name.startsWith(qNorm)) score += 3;
    score -= name.length / 200; // prefer shorter, more generic names
    score += opts.boost?.get(item.ref) ?? 0;
    scored.push({ item, score });
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, opts.limit ?? 30).map((s) => s.item);
}
